import React, { useState, useEffect } from 'react';
import { purchaseOrdersApi, suppliersApi, warehousesApi, productsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  ShoppingCart,
  Loader2,
  Trash2,
  Send,
  PackageCheck,
  XCircle,
  Eye
} from 'lucide-react';

const PurchaseOrdersPage = () => {
  const { isOperator, isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [receiveDialog, setReceiveDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ total: 0 });
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    warehouse_id: '',
    notes: '',
    lines: [{ product_id: '', quantity_ordered: '', unit_cost: '' }]
  });

  const [receiveData, setReceiveData] = useState([]);

  const statusConfig = {
    DRAFT: { label: 'Borrador', className: 'bg-slate-100 text-slate-700' },
    SENT: { label: 'Enviada', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    PARTIALLY_RECEIVED: { label: 'Parcial', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    RECEIVED: { label: 'Recibida', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    CANCELED: { label: 'Cancelada', className: 'bg-red-50 text-red-700 border-red-200' }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrdersApi.list({ limit: 100 });
      setOrders(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const [suppliersRes, warehousesRes, productsRes] = await Promise.all([
        suppliersApi.list({ is_active: true }),
        warehousesApi.list(),
        productsApi.list({ is_active: true })
      ]);
      setSuppliers(suppliersRes.data.data);
      setWarehouses(warehousesRes.data.data);
      setProducts(productsRes.data.data);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchCatalogs();
  }, []);

  const openCreateDialog = () => {
    setFormData({
      supplier_id: '',
      warehouse_id: '',
      notes: '',
      lines: [{ product_id: '', quantity_ordered: '', unit_cost: '' }]
    });
    setDialogOpen(true);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { product_id: '', quantity_ordered: '', unit_cost: '' }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length === 1) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        supplier_id: formData.supplier_id,
        warehouse_id: formData.warehouse_id,
        notes: formData.notes,
        lines: formData.lines.map(line => ({
          product_id: line.product_id,
          quantity_ordered: parseFloat(line.quantity_ordered),
          unit_cost: parseFloat(line.unit_cost) || 0
        }))
      };

      await purchaseOrdersApi.create(payload);
      toast.success('Orden de compra creada');
      setDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al crear orden');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (order) => {
    try {
      await purchaseOrdersApi.send(order.id);
      toast.success('Orden enviada');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al enviar');
    }
  };

  const handleCancel = async (order) => {
    if (!window.confirm('¿Cancelar esta orden de compra?')) return;
    try {
      await purchaseOrdersApi.cancel(order.id);
      toast.success('Orden cancelada');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al cancelar');
    }
  };

  const openReceiveDialog = (order) => {
    setReceiveData(order.lines.filter(l => !l.is_complete).map(line => ({
      line_id: line.id,
      product_name: line.product_name,
      pending: line.pending_quantity,
      quantity_received: '',
      actual_unit_cost: line.unit_cost
    })));
    setReceiveDialog(order);
  };

  const handleReceive = async () => {
    const linesToReceive = receiveData.filter(l => l.quantity_received && parseFloat(l.quantity_received) > 0);
    if (linesToReceive.length === 0) {
      toast.error('Ingrese cantidades a recibir');
      return;
    }

    setSaving(true);
    try {
      await purchaseOrdersApi.receive(receiveDialog.id, {
        lines: linesToReceive.map(l => ({
          line_id: l.line_id,
          quantity_received: parseFloat(l.quantity_received),
          actual_unit_cost: parseFloat(l.actual_unit_cost)
        })),
        notes: ''
      });
      toast.success('Recepción registrada');
      setReceiveDialog(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al recibir');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Órdenes de Compra</h1>
          <p className="text-slate-500 mt-1">Gestiona las compras y recepciones</p>
        </div>
        {isOperator() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="create-po-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Orden
          </Button>
        )}
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Lista de Órdenes
            <Badge variant="secondary" className="ml-2">{meta.total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay órdenes de compra</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4"># Orden</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Proveedor</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Almacén</th>
                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Estado</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Total Est.</th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.DRAFT;
                    return (
                      <tr key={order.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="text-sm font-mono font-medium text-blue-900">{order.order_number}</span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900">{order.supplier_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{order.warehouse_name}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge className={status.className}>{status.label}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900 text-right tabular-nums font-medium">
                          {formatCurrency(order.total_estimated)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailDialog(order)}
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {order.status === 'DRAFT' && isOperator() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSend(order)}
                                className="text-blue-600"
                                title="Enviar"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                            {['SENT', 'PARTIALLY_RECEIVED'].includes(order.status) && isOperator() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReceiveDialog(order)}
                                className="text-emerald-600"
                                title="Recibir"
                              >
                                <PackageCheck className="w-4 h-4" />
                              </Button>
                            )}
                            {['DRAFT', 'SENT'].includes(order.status) && isAdmin() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancel(order)}
                                className="text-red-600"
                                title="Cancelar"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Orden de Compra</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} - {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Almacén Destino</Label>
                <Select
                  value={formData.warehouse_id}
                  onValueChange={(value) => setFormData({ ...formData, warehouse_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar almacén" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.code} - {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Lines */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Líneas de la orden</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>

              {formData.lines.map((line, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-md">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-slate-700">Producto {index + 1}</span>
                    {formData.lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Producto</Label>
                      <Select
                        value={line.product_id}
                        onValueChange={(value) => updateLine(index, 'product_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.sku} - {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Cantidad</Label>
                      <Input
                        type="number"
                        min="1"
                        value={line.quantity_ordered}
                        onChange={(e) => updateLine(index, 'quantity_ordered', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Costo Unitario</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.unit_cost}
                        onChange={(e) => updateLine(index, 'unit_cost', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-900 hover:bg-blue-800"
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Crear Orden
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Orden {detailDialog?.order_number}</DialogTitle>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Proveedor</p>
                  <p className="font-medium">{detailDialog.supplier_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Almacén</p>
                  <p className="font-medium">{detailDialog.warehouse_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Estado</p>
                  <Badge className={statusConfig[detailDialog.status]?.className}>
                    {statusConfig[detailDialog.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500">Total Estimado</p>
                  <p className="font-medium">{formatCurrency(detailDialog.total_estimated)}</p>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Líneas de la orden</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Producto</th>
                      <th className="text-right py-2">Ordenado</th>
                      <th className="text-right py-2">Recibido</th>
                      <th className="text-right py-2">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailDialog.lines.map((line, idx) => (
                      <tr key={idx} className="border-b border-slate-50">
                        <td className="py-2">{line.product_name || line.product_sku}</td>
                        <td className="py-2 text-right tabular-nums">{line.quantity_ordered}</td>
                        <td className="py-2 text-right tabular-nums">{line.quantity_received}</td>
                        <td className="py-2 text-right tabular-nums font-medium">
                          {line.pending_quantity}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={!!receiveDialog} onOpenChange={() => setReceiveDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Recibir Orden {receiveDialog?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Ingrese las cantidades recibidas para cada producto
            </p>
            {receiveData.map((item, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{item.product_name}</span>
                  <span className="text-sm text-slate-500">Pendiente: {item.pending}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Cantidad a recibir</Label>
                    <Input
                      type="number"
                      min="0"
                      max={item.pending}
                      value={item.quantity_received}
                      onChange={(e) => {
                        const newData = [...receiveData];
                        newData[idx].quantity_received = e.target.value;
                        setReceiveData(newData);
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Costo real</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.actual_unit_cost}
                      onChange={(e) => {
                        const newData = [...receiveData];
                        newData[idx].actual_unit_cost = e.target.value;
                        setReceiveData(newData);
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setReceiveDialog(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleReceive}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Registrar Recepción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
